// Design System Radio Component
// Unified radio button component

import React, { forwardRef } from 'react';
import { 
  Radio as MuiRadio,
  RadioGroup as MuiRadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  styled
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, transitionTokens } from '../../tokens';

// Styled Radio with design system tokens
const StyledRadio = styled(MuiRadio)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    color: colors.text.secondary,
    transition: transitionTokens.fast,
    
    '&.Mui-checked': {
      color: colors.primary.main,
    },
    
    '&:hover': {
      backgroundColor: `${colors.primary.main}14`,
    },
    
    '&.Mui-disabled': {
      color: colors.text.disabled,
    },
  };
});

// Styled FormControl wrapper
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: spacingTokens.component.md,
}));

/**
 * Radio - Single radio button component
 * Can be used standalone or within RadioGroup
 */
export const Radio = forwardRef((props, ref) => {
  return <StyledRadio ref={ref} {...props} />;
});

Radio.displayName = 'Radio';

/**
 * RadioGroup - Group of radio buttons
 * 
 * @example
 * // Basic usage
 * <RadioGroup
 *   label="Select plan"
 *   value={plan}
 *   onChange={setPlan}
 *   options={[
 *     { value: 'basic', label: 'Basic - $9/mo' },
 *     { value: 'pro', label: 'Pro - $19/mo' },
 *     { value: 'enterprise', label: 'Enterprise - Contact us' }
 *   ]}
 * />
 * 
 * // Horizontal layout
 * <RadioGroup
 *   label="Gender"
 *   value={gender}
 *   onChange={setGender}
 *   row
 *   options={[
 *     { value: 'male', label: 'Male' },
 *     { value: 'female', label: 'Female' },
 *     { value: 'other', label: 'Other' }
 *   ]}
 * />
 */
export const RadioGroup = forwardRef(({
  label,
  options = [],
  error,
  helperText,
  row = false,
  ...props
}, ref) => {
  return (
    <StyledFormControl error={Boolean(error)}>
      {label && (
        <FormLabel component="legend">{label}</FormLabel>
      )}
      <MuiRadioGroup ref={ref} row={row} {...props}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<StyledRadio />}
            label={option.label}
            disabled={option.disabled}
          />
        ))}
      </MuiRadioGroup>
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
});

RadioGroup.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    disabled: PropTypes.bool,
  })),
  error: PropTypes.bool,
  helperText: PropTypes.node,
  row: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

RadioGroup.displayName = 'RadioGroup';

export default Radio;